// @spec DHB-STR-008
'use client';

import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { AlertCircle, ArrowLeft, Ban, Loader2 } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { useCreateEntry, useDatapoint, useEntryLookup, useUpdateEntry } from '@/features/datahub';
import { DynamicDataEntryForm } from '@/features/datahub/entry';
import type { DataEntryFormData } from '@/features/datahub/entry';
import { useOrgPeriods } from '@/features/datahub/periods';
import { useResolveCurrentPeriod } from '@/features/datahub/periods/api/use-resolve-period';
import type { DataEntry } from '@/features/datahub/types';
import i18n from '@/lib/i18n';
import { ApiClientError } from '@/types/api';
import { useTranslation } from 'react-i18next';

export const Route = createFileRoute('/_authenticated/datahub/datapoints/$datapointId/entry')({
  beforeLoad: () => ({
    getTitle: () => i18n.t('routeTitles.enterData'),
  }),
  component: DataEntryPage,
});

/**
 * Data entry page for entering values for a specific datapoint.
 * Dynamically renders the appropriate form based on the datapoint's configuration.
 */
function DataEntryPage() {
  const { t } = useTranslation(['datahub', 'common']);
  const { datapointId } = Route.useParams();
  const navigate = useNavigate();

  // Selected period state
  const [selectedPeriodId, setSelectedPeriodId] = useState<string>('');

  // Fetch datapoint details
  const {
    datapoint,
    isLoading: isLoadingDatapoint,
    isError,
    error,
  } = useDatapoint({
    datapointId,
  });

  // Resolve current period (find-or-create)
  const { data: resolvedPeriod } = useResolveCurrentPeriod();

  // Fetch reporting periods
  const periodsQuery = useOrgPeriods({ pageSize: 100 });
  const periods = periodsQuery.data?.items ?? [];

  // Auto-select resolved period, fallback to first in list
  const fallbackPeriodId = resolvedPeriod?.id ?? periods[0]?.id ?? '';
  const effectivePeriodId = selectedPeriodId || fallbackPeriodId;

  // Check for existing entry
  const { entry: existingEntry, isLoading: isLoadingEntry } = useEntryLookup({
    datapointId,
    reportingPeriodId: effectivePeriodId,
    enabled: !!effectivePeriodId,
  });

  // Mutations
  const createEntry = useCreateEntry({
    onSuccess: () => {
      toast.success(t('datahub:toast.dataSavedSuccessfully'));
    },
    onError: (error) => {
      if (error instanceof ApiClientError && error.details?.errors) {
        const errors = error.details.errors as { message: string }[];
        toast.error(errors.map((e) => e.message).join(', '));
      } else {
        toast.error(error.message || i18n.t('datahub:toast.failedToSaveData'));
      }
    },
  });

  const updateEntry = useUpdateEntry({
    onSuccess: () => {
      toast.success(t('datahub:toast.dataUpdatedSuccessfully'));
    },
    onError: (error) => {
      if (error instanceof ApiClientError && error.details?.errors) {
        const errors = error.details.errors as { message: string }[];
        toast.error(errors.map((e) => e.message).join(', '));
      } else {
        toast.error(error.message || i18n.t('datahub:toast.failedToUpdateData'));
      }
    },
  });

  // Handle form submission
  const handleSubmit = async (data: DataEntryFormData): Promise<DataEntry> => {
    if (!effectivePeriodId) {
      throw new Error('Please select a reporting period');
    }

    if (existingEntry) {
      // Update existing entry
      return updateEntry.mutateAsync({
        entryId: existingEntry.id,
        value: data.value,
        notes: data.notes,
      });
    } else {
      // Create new entry
      return createEntry.mutateAsync({
        datapointId,
        reportingPeriodId: effectivePeriodId,
        value: data.value,
        notes: data.notes,
      });
    }
  };

  // Handle success — stay on entry page so user can continue entering data
  const handleSuccess = () => {
    // no navigation; toast already shown by mutation onSuccess
  };

  // Handle cancel
  const handleCancel = () => {
    void navigate({ to: '/datahub/datapoints/$datapointId', params: { datapointId } });
  };

  // Error states
  const isNotFound = error instanceof ApiClientError && error.status === 404;
  const isForbidden = error instanceof ApiClientError && error.status === 403;

  const isPeriodLocked = false as boolean; // placeholder for future period locking

  // Loading state
  const isLoading = isLoadingDatapoint || periodsQuery.isLoading;
  const isSubmitting = createEntry.isPending || updateEntry.isPending;

  return (
    <div className="p-8">
      <div className="mx-auto max-w-2xl space-y-6">
        {/* Breadcrumb */}
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink asChild>
                <Link to="/datahub/datapoints">Data Hub</Link>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbLink asChild>
                <Link to="/datahub/datapoints/$datapointId" params={{ datapointId }}>
                  {datapoint?.name ?? 'Datapoint'}
                </Link>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage>Enter Data</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>

        {/* Back button */}
        <Button
          variant="ghost"
          size="sm"
          onClick={() => {
            void navigate({ to: '/datahub/datapoints/$datapointId', params: { datapointId } });
          }}
          className="-ml-2"
        >
          <ArrowLeft className="mr-2 h-4 w-4" aria-hidden="true" />
          Back to Datapoint
        </Button>

        {/* Loading state */}
        {isLoading && (
          <Card>
            <CardHeader>
              <Skeleton className="h-6 w-48" />
              <Skeleton className="h-4 w-72" />
            </CardHeader>
            <CardContent className="space-y-4">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-20 w-full" />
            </CardContent>
          </Card>
        )}

        {/* Not found error */}
        {isNotFound && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Datapoint not found</AlertTitle>
            <AlertDescription>
              The datapoint you are looking for does not exist or has been deleted.
            </AlertDescription>
            <Button variant="outline" size="sm" className="mt-4" onClick={handleCancel}>
              Return to Catalog
            </Button>
          </Alert>
        )}

        {/* Forbidden error */}
        {isForbidden && (
          <Alert variant="destructive">
            <Ban className="h-4 w-4" />
            <AlertTitle>Access denied</AlertTitle>
            <AlertDescription>
              You do not have permission to enter data for this datapoint.
            </AlertDescription>
            <Button variant="outline" size="sm" className="mt-4" onClick={handleCancel}>
              Return to Catalog
            </Button>
          </Alert>
        )}

        {/* Generic error */}
        {isError && !isNotFound && !isForbidden && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>{t('common:routeErrors.errorLoadingDatapoint')}</AlertTitle>
            <AlertDescription>
              {error instanceof Error ? error.message : 'Failed to load datapoint details'}
            </AlertDescription>
          </Alert>
        )}

        {/* Main content */}
        {datapoint && !isLoading && (
          <Card>
            <CardHeader>
              <CardTitle>Enter Data: {datapoint.name}</CardTitle>
              <CardDescription>
                {datapoint.description ?? `Enter a value for ${datapoint.code}`}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Period selector */}
              <div className="space-y-2">
                <Label htmlFor="period-select">Reporting Period</Label>
                <Select
                  options={periods.map((p) => ({
                    value: p.id,
                    label: p.name,
                  }))}
                  placeholder="Select a reporting period"
                  value={effectivePeriodId}
                  onChange={(value) => {
                    setSelectedPeriodId(value);
                  }}
                  disabled={isSubmitting}
                />
                {isPeriodLocked && (
                  <p className="text-sm text-amber-600">
                    This period is locked. Data cannot be modified.
                  </p>
                )}
              </div>

              {/* Loading entry state */}
              {isLoadingEntry && effectivePeriodId && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Checking for existing data...
                </div>
              )}

              {/* Existing entry notice */}
              {existingEntry && !isLoadingEntry && (
                <Alert>
                  <AlertDescription>
                    A value already exists for this period. Saving will update the existing entry.
                  </AlertDescription>
                </Alert>
              )}

              {/* Data entry form */}
              {effectivePeriodId && !isPeriodLocked && (
                <DynamicDataEntryForm
                  datapoint={datapoint}
                  reportingPeriodId={effectivePeriodId}
                  existingEntry={existingEntry ?? null}
                  onSuccess={handleSuccess}
                  onCancel={handleCancel}
                  onSubmit={handleSubmit}
                  isSubmitting={isSubmitting}
                />
              )}

              {/* Locked period message */}
              {isPeriodLocked && (
                <div className="flex justify-end">
                  <Button variant="outline" onClick={handleCancel}>
                    Cancel
                  </Button>
                </div>
              )}

              {/* No period selected message */}
              {!effectivePeriodId && periods.length > 0 && (
                <p className="text-sm text-muted-foreground">
                  Please select a reporting period to enter data.
                </p>
              )}

              {/* No periods available */}
              {!periodsQuery.isLoading && periods.length === 0 && (
                <Alert>
                  <AlertDescription>
                    No reporting periods are configured. Please create a reporting period first.
                  </AlertDescription>
                </Alert>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
